/**
 * Copyright (C) 2017 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

// TODO: This should inherit from LuiCheckbox or share a common base class with
//       LuiCheckbox. For now, the code is copied for a proof of concept of the
//       LuiCheckbox component.

import { LuiInputBase } from '@here/lui-base'
import scopedCss from 'lui-radiobutton.scss'
import template from 'lui-radiobutton.html'

export default class LuiRadiobutton extends LuiInputBase {
  static get is() {
    return 'lui-radiobutton'
  }

  static get attributeToggles() {
    return [
      'checked',
      'disabled',
      'name',
      'required',
      'tabindex',
      'value',
    ]
  }

  static get clickTriggers() {
    return ['space']
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  constructor() {
    super()
    this.syncedProperties = ['name', 'checked', 'value', 'disabled']
  }

  init() {
    super.init()
    this.label.style.display = this.hasChildNodes() ? 'block' : 'none'
  }

  onConnected() {
    super.onConnected()
    this._onFieldClickBound = this._onFieldClickBound || ((e) => this._onFieldClick(e))
    this._onWrapperClickBound = this._onWrapperClickBound || ((e) => this._onWrapperClick(e))
    this.wrapper.addEventListener('click', this._onWrapperClickBound)
    this.delay(() => {
      this._getField()
      this.field.addEventListener('change', this._onFieldClickBound)
    })
  }

  onDisconnected() {
    super.onDisconnected()
    this.wrapper.removeEventListener('click', this._onWrapperClickBound)
    this.field.removeEventListener('change', this._onFieldClickBound)
  }

  _onFieldClick(e) {
    if (this.disabled) return
    if (this._ignoreChange) e.stopPropagation()
    this._readField()
  }

  _onWrapperClick() {
    if (this._ignoreChange) return
    const event = new window.MouseEvent('click')
    this.field.dispatchEvent(event)
  }

  _name() {
    let e
    return this.getAttribute('name') || ((e = this.querySelector('input')) && e.name)
  }

  _readField(field) {
    field = field || this.field
    this.syncedProperties.forEach((property) => {
      const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(field))[property]
      const value = nativeImplementation ? field[property] : field.getAttribute(property)
      if (value) {
        this.setAttribute(property, value === true ? '' : value)
      } else {
        this.removeAttribute(property)
      }
    })
  }

  _writeField(field) {
    field = field || this.field
    this.syncedProperties.forEach((property) => {
      // field[property] = this[property]
    })
    const event = new Event('change')
    this.dispatchEvent(event)
  }

  disabledChanged() {
    super.disabledChanged()
    const disabled = this.disabled
    this.toggleCssClass('lui-disabled', disabled)
    this.wrapper.classList[disabled ? 'remove' : 'add']('lui-interactive')
  }

  checkedChanged(oldVal, newVal) {
    const checked = this.hasAttribute('checked')
    this.toggleCssClass('lui-checked', checked)
    if (checked) {
      // TODO: document won’t work inside a Shadow DOM (when the Checkbox is nested within another
      //       component)
      let selector = 'lui-radiobutton[checked][name="' + this._name() + '"]'
      let radiobuttons = document.querySelectorAll(selector)
      if (radiobuttons.length === 0) {
        const group = this.closest('lui-radiobutton-group')
        if (group) {
          radiobuttons = group.querySelectorAll('lui-radiobutton')
        }
      }
      if (radiobuttons.length > 0) {
        radiobuttons.forEach((e) => {
          if (e !== this) e.checked = false
        })
      }
    }
    this._writeField()
  }

  valueChanged() {
    this._writeField()
  }

  _getField() {
    this._field = this.querySelector('input')
    this._ignoreChange = true
    const temporaryField = this.shadowRoot.querySelector('input')
    if (this._field) {
      if (temporaryField) temporaryField.parentNode.removeChild(temporaryField)
      if (this._field.type !== 'radio') console.error(`<input type="${this._field.type}"> is not supported.`)
      if (!this.fieldListenerInitialized) this._addPropertyListenerToField(this._field)
      this.fieldListenerInitialized = true
      this._readField(this._field)
      this._writeField(this._field)
    } else if (temporaryField) {
      this._field = temporaryField
      this._writeField(this._field)
    } else {
      const field = this._createField()
      this._writeField(field)
      this._field = field
    }
    this._ignoreChange = false
    return this._field
  }

  _createField() {
    const element = document.createElement('input')
    element.type = 'radio'
    this.label.appendChild(element)
    return element
  }

  _addPropertyListenerToField(field) {
    this.syncedProperties.forEach((property) => {
      const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(field))[property]
      if (!nativeImplementation) return
      Object.defineProperty(field, property, {
        set: (value) => {
          nativeImplementation.set.call(field, value)
          if (this._ignoreChange) return
          this._ignoreChange = true
          this._readField()
          this._ignoreChange = false
        },
        get: nativeImplementation.get,
      })
    })
  }

  get label() {
    return this._label || (this._label = this.shadowRoot.querySelector('.lui-checkbox__label'))
  }

  get indicator() {
    return this._indicator || (this._indicator = this.shadowRoot.querySelector('.lui-checkbox__indicator'))
  }

  get wrapper() {
    return this._wrapper || (this._wrapper = this.shadowRoot.querySelector('.lui-checkbox__wrapper'))
  }

  get value() {
    return this.field ? this.field.value : this.getAttribute('value')
  }

  set value(newValue) {
    this.setAttribute('value', newValue)
    this.valueChanged('', newValue)
  }

  get checked() {
    return this._field ? this._field.checked : this.hasAttribute('checked')
  }

  set checked(newValue) {
    this.field.checked = newValue
    if (newValue) {
      this.setAttribute('checked', '')
    } else {
      this.removeAttribute('checked')
    }
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-radiobutton/lui-radiobutton.js
// module id = 291
// module chunks = 0 1 15